/**
 * Utilitários para gerenciamento de cache
 * Centraliza a lógica de limpeza de cache para evitar duplicação
 */

/**
 * Limpa o cache da aplicação de forma seletiva
 * Preserva dados importantes como sessões e configurações do usuário
 * @param {string} context - Contexto da limpeza (logout, update, etc.)
 */
export const clearApplicationCache = (context = 'manual') => {
  console.log(`🗑️ Iniciando limpeza de cache (${context})...`);
  
  // Limpar cache via Service Worker
  if ('serviceWorker' in navigator && 'caches' in window) {
    // Enviar mensagem para o service worker limpar cache
    navigator.serviceWorker.ready.then((registration) => {
      if (registration.active) {
        registration.active.postMessage({ type: 'CLEAR_CACHE' });
        console.log('📤 Mensagem enviada para Service Worker');
      }
    }).catch((error) => {
      console.log('⚠️ Service Worker não disponível:', error);
    });
    
    // Também limpar caches diretamente via Cache API
    caches.keys().then(names => {
      const cachesToClear = names.filter(name => 
        name.includes('app-cache-') || 
        name.includes('static') || 
        name.includes('workbox') ||
        name.includes('manifest-cache') ||
        name.includes('images')
      );
      
      return Promise.all(
        cachesToClear.map(name => {
          console.log(`🗑️ Removendo cache (${context}):`, name);
          return caches.delete(name);
        })
      );
    }).then(() => {
      console.log(`✅ Caches removidos com sucesso (${context})`);
    }).catch((error) => {
      console.log('⚠️ Erro ao limpar caches:', error);
    });
  }
  
  // Limpar localStorage específico de cache
  clearCacheLocalStorage(context);
};

/**
 * Limpa apenas as chaves do localStorage relacionadas ao cache
 * Preserva dados importantes como token, configurações do usuário, etc.
 * @param {string} context - Contexto da limpeza
 */
export const clearCacheLocalStorage = (context = 'manual') => {
  try {
    const keysToRemove = [];
    
    // Identificar chaves relacionadas ao cache
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key === 'app_version' || 
        key === 'app_cache_version' || 
        key === 'build_timestamp' ||
        key.startsWith('cache_') ||
        key.startsWith('version_') ||
        key.includes('workbox')
      )) {
        keysToRemove.push(key);
      }
    }
    
    // Remover as chaves identificadas
    keysToRemove.forEach(key => {
      console.log(`🗑️ Removendo localStorage (${context}):`, key);
      localStorage.removeItem(key);
    });
    
    if (keysToRemove.length > 0) {
      console.log(`✅ ${keysToRemove.length} chaves de cache removidas do localStorage (${context})`);
    } else {
      console.log(`ℹ️ Nenhuma chave de cache encontrada no localStorage (${context})`);
    }
  } catch (error) {
    console.log(`⚠️ Erro ao limpar localStorage (${context}):`, error);
  }
};

/**
 * Força um reload completo da aplicação após limpeza de cache
 * @param {number} delay - Delay em ms antes do reload (padrão: 500ms)
 */
export const forceReloadAfterCacheClear = (delay = 500) => {
  console.log(`🔄 Recarregando aplicação em ${delay}ms...`);
  setTimeout(() => {
    window.location.reload(true);
  }, delay);
};

/**
 * Verifica se o cache está disponível no navegador
 * @returns {boolean} - True se cache API estiver disponível
 */
export const isCacheAvailable = () => {
  return 'caches' in window && 'serviceWorker' in navigator;
};

/**
 * Obtém informações sobre os caches ativos
 * @returns {Promise<Array>} - Lista de nomes dos caches
 */
export const getCacheInfo = async () => {
  if (!isCacheAvailable()) {
    return [];
  }
  
  try {
    const cacheNames = await caches.keys();
    console.log('📊 Caches ativos:', cacheNames);
    return cacheNames;
  } catch (error) {
    console.log('⚠️ Erro ao obter informações de cache:', error);
    return [];
  }
};